import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Building2, Warehouse, Factory, Home, MapPin } from 'lucide-react';
import { renderToString } from 'react-dom/server';

// Fix pentru iconurile Leaflet default
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Componenta pentru a ajusta view-ul la markere
const FitBounds = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations.length > 0) {
      const validLocations = locations.filter(
        loc => loc.latitude && loc.longitude && 
        !isNaN(parseFloat(loc.latitude)) && !isNaN(parseFloat(loc.longitude))
      );
      
      if (validLocations.length > 0) {
        const bounds = L.latLngBounds(
          validLocations.map(loc => [parseFloat(loc.latitude), parseFloat(loc.longitude)])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [locations, map]);
  
  return null;
};

// Creare icon personalizat bazat pe tip
const createCustomIcon = (iconType, color = '#0052CC') => {
  const iconComponents = {
    factory: Factory,
    warehouse: Warehouse,
    office: Building2,
    home: Home,
    default: MapPin
  };
  
  const IconComponent = iconComponents[iconType] || iconComponents.default;
  
  const svgString = renderToString(
    <div style={{
      backgroundColor: color,
      borderRadius: '50%',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      border: '3px solid white'
    }}>
      <IconComponent style={{ width: '18px', height: '18px', color: 'white' }} />
    </div>
  );
  
  return L.divIcon({
    html: svgString,
    className: 'custom-map-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });
};

/**
 * LocationMap - Componenta de harta pentru afisarea locatiilor
 * 
 * Props:
 * - locations: Array de locatii cu latitude, longitude, name, etc.
 * - height: Inaltimea hartii (default: 400px)
 * - onMarkerClick: Callback la click pe marker
 * - selectedLocationId: ID-ul locatiei selectate
 * - showControls: Afiseaza controale zoom
 */
export const LocationMap = ({
  locations = [],
  height = '400px',
  onMarkerClick,
  selectedLocationId,
  showControls = true,
  className = ''
}) => {
  // Filtram locatiile cu coordonate valide
  const validLocations = locations.filter(
    loc => loc.latitude && loc.longitude && 
    !isNaN(parseFloat(loc.latitude)) && !isNaN(parseFloat(loc.longitude))
  );

  // Centrul default (Romania)
  const defaultCenter = [45.9432, 24.9668];
  const defaultZoom = 6;

  // Calculam centrul bazat pe locatii
  const center = validLocations.length > 0 
    ? [
        validLocations.reduce((sum, loc) => sum + parseFloat(loc.latitude), 0) / validLocations.length,
        validLocations.reduce((sum, loc) => sum + parseFloat(loc.longitude), 0) / validLocations.length
      ]
    : defaultCenter;

  const getMarkerColor = (location) => {
    if (location.id === selectedLocationId) return '#22C55E'; // Verde pentru selectat
    
    // Culori bazate pe tip
    const colors = {
      factory: '#0052CC',
      warehouse: '#F59E0B',
      office: '#8B5CF6',
      home: '#10B981',
      default: '#6366F1'
    };
    
    return colors[location.icon_type] || colors.default;
  };

  if (validLocations.length === 0) {
    return (
      <div 
        className={`flex items-center justify-center bg-slate-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-slate-500">
          <MapPin className="w-12 h-12 mx-auto mb-2 text-slate-400" />
          <p>Nu exista locatii cu coordonate GPS</p>
          <p className="text-sm">Adaugati latitudine si longitudine pentru a vizualiza pe harta</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden border ${className}`} style={{ height }}>
      <MapContainer
        center={center}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={showControls}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <FitBounds locations={validLocations} />
        
        {validLocations.map((location) => (
          <Marker
            key={location.id}
            position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
            icon={createCustomIcon(location.icon_type, getMarkerColor(location))}
            eventHandlers={{
              click: () => onMarkerClick?.(location)
            }}
          >
            <Popup>
              <div className="min-w-[200px]">
                <h3 className="font-semibold text-slate-900 mb-1">{location.name}</h3>
                {location.plant_name && (
                  <p className="text-sm text-slate-600 mb-1">
                    <Building2 className="w-3 h-3 inline mr-1" />
                    {location.plant_name}
                  </p>
                )}
                {location.description && (
                  <p className="text-sm text-slate-500 mb-2">{location.description}</p>
                )}
                <div className="text-xs text-slate-400 border-t pt-2 mt-2">
                  <p>Lat: {location.latitude}</p>
                  <p>Long: {location.longitude}</p>
                </div>
                {onMarkerClick && (
                  <button 
                    onClick={() => onMarkerClick(location)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Vezi detalii →
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

// CSS pentru marker-ele personalizate (adauga in index.css sau App.css)
const mapStyles = `
.custom-map-marker {
  background: transparent !important;
  border: none !important;
}
`;

// Injectam stilurile
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = mapStyles;
  document.head.appendChild(styleSheet);
}

export default LocationMap;
